import React, { useRef } from 'react';
import { Stage, Layer, Line, Rect, Arrow, Circle, Text, Transformer, Image } from 'react-konva';
import styles from './VideoPlayer.module.scss';

export type DrawingTool = 'line' | 'rect' | 'arrow' | 'circle' | 'text' | 'select' | 'image';

export interface Shape {
  id: string;
  tool: DrawingTool;
  points: number[];
  color: string;
  text?: string;
  imageUrl?: string;
}

interface DrawingCanvasProps {
  stageSize: { width: number; height: number };
  shapes: Shape[];
  tool: DrawingTool;
  color: string;
  selectedShapeId?: string | null;
  isDrawingEnabled: boolean;
  isDrawingToolboxOpen: boolean;
  onShapesChange: (shapes: Shape[]) => void;
  onTextToolClick: (pos: { x: number, y: number }) => void;
  onShapeSelection?: (shapeId: string | null) => void;
  onDrawingComplete?: () => void;
}

const DrawingCanvas: React.FC<DrawingCanvasProps> = ({
  stageSize,
  shapes,
  tool,
  color,
  selectedShapeId,
  isDrawingEnabled,
  isDrawingToolboxOpen,
  onShapesChange,
  onTextToolClick,
  onShapeSelection,
  onDrawingComplete,
}) => {
  const transformerRef = useRef<any>(null);
  const selectedShapeRef = useRef<any>(null);
  const isDrawingRef = useRef(false);
  const currentShapeIdRef = useRef<string | null>(null);

  // Yardımcı fonksiyonlar
  function normalizePoint(x: number, y: number, width: number, height: number) {
    return [x / width, y / height];
  }

  function denormalizePoint(nx: number, ny: number, width: number, height: number) {
    return [nx * width, ny * height];
  }

  function denormalizeShape(shape: Shape, width: number, height: number): Shape {
    const denormPoints = [];
    for (let i = 0; i < shape.points.length; i += 2) {
      const [x, y] = denormalizePoint(shape.points[i], shape.points[i + 1], width, height);
      denormPoints.push(x, y);
    }
    return { ...shape, points: denormPoints };
  }

  // Transformer'ı seçili şekle bağla
  React.useEffect(() => {
    if (selectedShapeRef.current && transformerRef.current) {
      transformerRef.current.nodes([selectedShapeRef.current]);
      transformerRef.current.getLayer().batchDraw();
    }
  }, [selectedShapeId]);

  // Resize işlemi sırasında şekli güncelle
  const handleTransform = () => {
    if (selectedShapeRef.current && selectedShapeId) {
      const node = selectedShapeRef.current;
      const scaleX = node.scaleX();
      const scaleY = node.scaleY();

      // Scale'i sıfırla ve points'leri güncelle
      node.scaleX(1);
      node.scaleY(1);

      const updatedShapes = shapes.map(shape => {
        if (shape.id === selectedShapeId) {
          let newPoints = [...shape.points];
          
          switch (shape.tool) {
            case 'rect':
              newPoints[2] = newPoints[0] + (newPoints[2] - newPoints[0]) * scaleX;
              newPoints[3] = newPoints[1] + (newPoints[3] - newPoints[1]) * scaleY;
              break;
            case 'circle':
              const [startX, startY, endX, endY] = newPoints;
              const radius = Math.sqrt(Math.pow(endX - startX, 2) + Math.pow(endY - startY, 2));
              const newRadius = radius * Math.max(scaleX, scaleY);
              newPoints[2] = startX + newRadius;
              newPoints[3] = startY + newRadius;
              break;
            case 'arrow':
              newPoints[2] = newPoints[0] + (newPoints[2] - newPoints[0]) * scaleX;
              newPoints[3] = newPoints[1] + (newPoints[3] - newPoints[1]) * scaleY;
              break;
            case 'line':
              // Line için tüm points'leri scale et
              for (let i = 0; i < newPoints.length; i += 2) {
                newPoints[i] = newPoints[0] + (newPoints[i] - newPoints[0]) * scaleX;
                newPoints[i + 1] = newPoints[1] + (newPoints[i + 1] - newPoints[1]) * scaleY;
              }
              break;
          }
          
          return { ...shape, points: newPoints };
        }
        return shape;
      });
      
      onShapesChange(updatedShapes);
    }
  };

  // Drawing Handlers
  const handleMouseDown = (e: any) => {
    if (!isDrawingEnabled || !isDrawingToolboxOpen) return;
    
    const pos = e.target.getStage().getPointerPosition();

    if (tool === 'text') {
      onTextToolClick(pos);
      return;
    }

    if (tool === 'select') {
      // Seçici modunda şekil seçme
      const clickedShape = e.target;
      
      // Eğer Transformer veya Transformer'ın child elementlerine tıklandıysa seçimi koru
      if (clickedShape && (clickedShape.className === 'Transformer' || 
                          clickedShape.getParent?.()?.className === 'Transformer' ||
                          clickedShape.hasName?.('_selectionRect') ||
                          clickedShape.hasName?.('_anchor'))) {
        return;
      }
      
      if (clickedShape && clickedShape.attrs && clickedShape.attrs.id) {
        onShapeSelection?.(clickedShape.attrs.id);
      } else {
        // Boş alana tıklandığında seçimi kaldır
        onShapeSelection?.(null);
      }
      return;
    }

    isDrawingRef.current = true;
    // Normalize et
    const [nx, ny] = normalizePoint(pos.x, pos.y, stageSize.width, stageSize.height);
    const newShape: Shape = {
      id: `shape-${Date.now()}`,
      tool,
      points: [nx, ny, nx, ny],
      color,
    };
    currentShapeIdRef.current = newShape.id;
    onShapesChange([...shapes, newShape]);
  };

  const handleMouseMove = (e: any) => {
    if (!isDrawingEnabled || !isDrawingToolboxOpen || !isDrawingRef.current) return;

    const stage = e.target.getStage();
    const point = stage.getPointerPosition();
    
    const [nx, ny] = normalizePoint(point.x, point.y, stageSize.width, stageSize.height);
    const updatedShapes = shapes.map(shape => {
      if (shape.id === currentShapeIdRef.current) {
        let newPoints = [...shape.points];
        if (shape.tool === 'line') {
          newPoints = [...newPoints, nx, ny];
        } else { // rect, arrow, circle
          newPoints[2] = nx;
          newPoints[3] = ny;
        }
        return { ...shape, points: newPoints };
      }
      return shape;
    });
    onShapesChange(updatedShapes);
  };

  const handleMouseUp = () => {
    if (!isDrawingEnabled || !isDrawingToolboxOpen) return;
    isDrawingRef.current = false;
    currentShapeIdRef.current = null;
    onDrawingComplete?.();
  };

  const handleShapeClick = (shapeId: string) => {
    if (tool === 'select') {
      onShapeSelection?.(shapeId);
    }
  };

  // Drag işlemi sırasında şekli güncelle
  const handleShapeDragMove = (e: any, shapeId: string) => {
    const node = e.target;
    const { x, y } = node.position();
    setShapePosition(shapeId, x, y);
  };

  // Drag işlemi bittiğinde şekli güncelle
  const handleShapeDragEnd = (e: any, shapeId: string) => {
    const node = e.target;
    const { x, y } = node.position();
    setShapePosition(shapeId, x, y);
  };

  // Belirli bir shape'in pozisyonunu güncelle
  const setShapePosition = (shapeId: string, x: number, y: number) => {
    const updatedShapes = shapes.map(shape => {
      if (shape.id === shapeId) {
        let newPoints = [...shape.points];
        switch (shape.tool) {
          case 'rect':
          case 'arrow': {
            // Pozisyonu normalize et
            const nx = x / stageSize.width;
            const ny = y / stageSize.height;
            const dx = nx - newPoints[0];
            const dy = ny - newPoints[1];
            newPoints[0] = nx;
            newPoints[1] = ny;
            newPoints[2] += dx;
            newPoints[3] += dy;
            break;
          }
          case 'circle': {
            const nx = x / stageSize.width;
            const ny = y / stageSize.height;
            const dxC = nx - newPoints[0];
            const dyC = ny - newPoints[1];
            newPoints[0] = nx;
            newPoints[1] = ny;
            newPoints[2] += dxC;
            newPoints[3] += dyC;
            break;
          }
          case 'line': {
            const nx = x / stageSize.width;
            const ny = y / stageSize.height;
            const dxL = nx - newPoints[0];
            const dyL = ny - newPoints[1];
            for (let i = 0; i < newPoints.length; i += 2) {
              newPoints[i] += dxL;
              newPoints[i + 1] += dyL;
            }
            break;
          }
          case 'text': {
            const nx = x / stageSize.width;
            const ny = y / stageSize.height;
            newPoints[0] = nx;
            newPoints[1] = ny;
            break;
          }
        }
        return { ...shape, points: newPoints };
      }
      return shape;
    });
    onShapesChange(updatedShapes);
  };

  const renderShape = (shape: Shape) => {
    const isSelected = selectedShapeId === shape.id;
    const isDraggable = tool === 'select';
    const denormShape = denormalizeShape(shape, stageSize.width, stageSize.height);
    const commonProps = {
      stroke: shape.color,
      strokeWidth: isSelected ? 6 : 4,
      lineCap: 'round',
      lineJoin: 'round',
      draggable: isDraggable,
      id: shape.id,
      onClick: () => handleShapeClick(shape.id),
      onTap: () => handleShapeClick(shape.id),
      ref: isSelected ? selectedShapeRef : undefined,
      ...(isDraggable && {
        onDragMove: (e: any) => handleShapeDragMove(e, shape.id),
        onDragEnd: (e: any) => handleShapeDragEnd(e, shape.id),
      })
    } as const;

    switch (shape.tool) {
      case 'line':
        return <Line key={shape.id} {...commonProps} points={denormShape.points} tension={0.5} />;
      case 'rect':
        return (
          <Rect
            key={shape.id}
            {...commonProps}
            x={denormShape.points[0]}
            y={denormShape.points[1]}
            width={denormShape.points[2] - denormShape.points[0]}
            height={denormShape.points[3] - denormShape.points[1]}
          />
        );
      case 'arrow':
         return (
          <Arrow
            key={shape.id}
            {...commonProps}
            points={denormShape.points}
            pointerLength={10}
            pointerWidth={10}
          />
        );
      case 'circle': {
        const [startX, startY, endX, endY] = denormShape.points;
        const radius = Math.sqrt(Math.pow(endX - startX, 2) + Math.pow(endY - startY, 2));
        return <Circle key={shape.id} {...commonProps} x={startX} y={startY} radius={radius} />;
      }
      case 'text':
        return (
          <Text
            key={shape.id}
            {...commonProps}
            x={denormShape.points[0]}
            y={denormShape.points[1]}
            text={shape.text}
            fontSize={24}
            fill={shape.color}
            strokeWidth={0} // No stroke for text
          />
        );
      case 'image':
        if (shape.imageUrl) {
          const [x, y, width, height] = denormShape.points;
          return (
            <Image
              key={shape.id}
              {...commonProps}
              x={x}
              y={y}
              width={width - x}
              height={height - y}
              image={new window.Image()}
              onLoad={(e: any) => {
                const img = e.target as HTMLImageElement;
                img.src = shape.imageUrl!;
              }}
            />
          );
        }
        return null;
      default:
        return null;
    }
  };

  return (
    <Stage
      width={stageSize.width}
      height={stageSize.height}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      className={styles.konvaStage}
    >
      <Layer>
        {shapes.map(renderShape)}
        {tool === 'select' && selectedShapeId && (
          <Transformer
            ref={transformerRef}
            boundBoxFunc={(oldBox, newBox) => {
              // Minimum boyut kontrolü
              if (newBox.width < 5 || newBox.height < 5) {
                return oldBox;
              }
              // Stage sınırları
              const stageWidth = stageSize.width;
              const stageHeight = stageSize.height;
              if (
                newBox.x < 0 ||
                newBox.y < 0 ||
                newBox.x + newBox.width > stageWidth ||
                newBox.y + newBox.height > stageHeight
              ) {
                return oldBox;
              }
              return newBox;
            }}
            onTransform={handleTransform}
          />
        )}
      </Layer>
    </Stage>
  );
};

export default DrawingCanvas; 